import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, RefreshCw, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { TransparencyPanel } from "@/components/TransparencyPanel";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/lib/i18n";
import HoldingDialog from "@/components/portfolio/HoldingDialog";
import RecordSaleModal from "@/components/portfolio/RecordSaleModal";
import ClosedPositionsTable from "@/components/portfolio/ClosedPositionsTable";
import TransactionHistoryModal from "@/components/portfolio/TransactionHistoryModal";
import PLBreakdownCard from "@/components/portfolio/PLBreakdownCard";
import PortfolioAIReview from "@/components/portfolio/PortfolioAIReview";
import LockedPortfolioPreview from "@/components/portfolio/LockedPortfolioPreview";
import EmptyPortfolioState from "@/components/portfolio/EmptyPortfolioState";
import ScoringHelpDialog from "@/components/portfolio/ScoringHelpDialog";
import PortfolioExportMenu from "@/components/portfolio/PortfolioExportMenu";
import PortfolioDashboardView from "@/components/portfolio/PortfolioDashboardView";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatCooldown(t, nextAt) {
  if (!nextAt) return null;
  try {
    const diffMs = new Date(nextAt).getTime() - Date.now();
    if (diffMs <= 0) return null;
    const minutes = Math.ceil(diffMs / 60000);
    return t("portfolio.refresh.next_in_minutes").replace("{n}", String(minutes));
  } catch {
    return null;
  }
}

export default function Portfolio() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);
  // Phase 8.B QA-11 — request-order protection. We track the latest
  // review fingerprint (review.generated_at + _selected_id) ever
  // rendered AND a monotonically-increasing fetch sequence id. Any
  // later /api/portfolio response is ONLY allowed to replace `data`
  // when:
  //   * it carries a strictly newer review (or no review at all), OR
  //   * the user explicitly triggered a refresh (`forceAcceptNext`).
  // This guarantees that an in-flight GET that happened to start
  // before a successful refresh-analysis cannot clobber the freshly
  // generated review with an older cached row when it eventually
  // resolves.
  const fetchSeqRef = useRef(0);
  const latestReviewRef = useRef({ generatedAt: null, selectedId: null });
  const forceAcceptNextRef = useRef(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [dialogInitial, setDialogInitial] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [refreshPricesBusy, setRefreshPricesBusy] = useState(false);
  const [refreshAnalysisBusy, setRefreshAnalysisBusy] = useState(false);
  const [scoringHelpOpen, setScoringHelpOpen] = useState(false);
  // Phase 8.C-2 — Record Sale modal state.
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleTarget, setSaleTarget] = useState(null);   // open holding row
  const [saleBusy, setSaleBusy] = useState(false);
  const [saleError, setSaleError] = useState(null);
  // Phase 8.C-2 — Transaction History modal state.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTicker, setHistoryTicker] = useState(null);
  const [historyLifecycleId, setHistoryLifecycleId] = useState(null);
  const [historyLifecycleStatus, setHistoryLifecycleStatus] = useState("open");
  const [historyCurrency, setHistoryCurrency] = useState("USD");
  const [performanceRevision, setPerformanceRevision] = useState(0);
  // Phase 8.C-2 — Closed positions cache (fetched on mount + after every sale).
  const [closedPositions, setClosedPositions] = useState([]);
  // Phase 8.C-2 — extra delete metadata for the "this position has N
  // transactions" stronger warning. Populated when the user clicks the
  // "Delete Incorrect Position" action.
  const [deleteTxnCount, setDeleteTxnCount] = useState(0);

  const fetchPortfolio = useCallback(async () => {
    // Phase 8.B QA-11 — claim a fresh sequence id BEFORE awaiting. Any
    // older in-flight fetch whose seq is below the current value MUST
    // be ignored on resolution.
    const mySeq = ++fetchSeqRef.current;
    try {
      const res = await api.get("/portfolio");
      // Phase 8.1.b defensive parsing — SafeJSONRoute returns 500 with
      // {success:false, error:{...}} for any unhandled exception.
      // axios surfaces 5xx as a rejection, so reaching this point means
      // either a successful 200 or a 200-but-shaped-as-error payload.
      // Guard against both for forward compatibility.
      if (res?.data?.success === false) {
        const errType = res?.data?.error?.type;
        setFetchError(
          errType === "auth_error" ? t("portfolio.error.load_failed")
            : t("portfolio.error.load_failed"),
        );
        return;
      }
      // Phase 8.B QA-11 — request-order guard. Discard the response if
      // a NEWER fetchPortfolio has already been started, OR if this
      // response carries an older review than what we've already
      // rendered and we are NOT in a forced-accept window.
      if (mySeq < fetchSeqRef.current) {
        // A newer fetch is in flight — ignore this stale resolution.
        return;
      }
      const incomingReview = res?.data?.review || null;
      const incomingGeneratedAt = incomingReview?.generated_at || null;
      const incomingSelectedId = incomingReview?._selected_id || null;
      const lastGenAt = latestReviewRef.current.generatedAt;
      // If we have a previously-rendered review and the incoming one
      // is older (strictly less than), suppress unless the user just
      // triggered an explicit refresh that demands this response.
      if (
        !forceAcceptNextRef.current
        && lastGenAt
        && incomingGeneratedAt
        && new Date(incomingGeneratedAt).getTime() < new Date(lastGenAt).getTime()
      ) {
        // The backend handed us a strictly older review than what is
        // currently on screen. This is the symptom of the "wrong
        // review restored after reload" production bug. Refuse to
        // overwrite — keep the newer review visible. We DO accept
        // the rest of the payload's non-review fields by leaving the
        // existing `data.review` intact via the merge below.
        setData((prev) => ({
          ...(prev || {}),
          ...res.data,
          review: prev?.review || res.data.review,
          review_meta: prev?.review_meta || res.data.review_meta,
        }));
        setFetchError(null);
        return;
      }
      // Accept the response. Update the latest-review fingerprint so
      // future fetches can detect regressions.
      if (incomingGeneratedAt || incomingSelectedId) {
        latestReviewRef.current = {
          generatedAt: incomingGeneratedAt,
          selectedId: incomingSelectedId,
        };
      }
      forceAcceptNextRef.current = false;
      setData(res.data);
      setFetchError(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        navigate("/login?next=/portfolio", { replace: true });
        return;
      }
      // Only surface a fetch error if this is still the latest fetch.
      if (mySeq === fetchSeqRef.current) {
        setFetchError(t("portfolio.error.load_failed"));
      }
    } finally {
      // Only flip `loadingPortfolio` off on the LATEST fetch — older
      // resolutions must not lift the loading spinner prematurely.
      if (mySeq === fetchSeqRef.current) {
        setLoadingPortfolio(false);
      }
    }
  }, [navigate, t]);

  const snapshotRetryRef = useRef(0);
  useEffect(() => {
    if (!data?.snapshot_refresh_pending) { snapshotRetryRef.current = 0; return; }
    if (snapshotRetryRef.current >= 10) return;
    const timer = setTimeout(() => {
      snapshotRetryRef.current += 1;
      fetchPortfolio();
    }, 3000);
    return () => clearTimeout(timer);
  }, [data, fetchPortfolio]);

  useEffect(() => {
    if (loading) return;
    // Phase 8.WLNAV.v1 — do not redirect logged-out visitors. Instead we
    // render the LockedPortfolioPreview so the marketing value of the
    // Portfolio surface remains visible without exposing real data.
    // Ensure the spinner doesn't hang forever when we short-circuit.
    if (!user) { setLoadingPortfolio(false); return; }
    let cancelled = false;
    Promise.resolve().then(() => { if (!cancelled) fetchPortfolio(); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.user_id]);

  const locked = data?.locked === true;
  const summary = data?.summary || {};
  // Sub-pass 2A (2026-02-17) — `positions` is a logical fallback
  // whose identity changes on every render because `data?.positions
  // || []` produces a new empty array every time when
  // `data.positions` is undefined.  Memoising the value stabilises
  // its identity so downstream `useMemo`s that depend on it
  // (dominantCurrency, holdings sort, etc.) do not thrash.
  const positions = useMemo(() => data?.positions || [], [data?.positions]);
  const snapshots = data?.snapshots || [];
  const review = data?.review || null;
  const refreshState = data?.refresh || {};
  const pricesNextAt = refreshState?.prices?.next_available_at;
  const analysisNextAt = refreshState?.analysis?.next_available_at;
  const refreshesLeft = refreshState?.analysis?.refreshes_left_today ?? null;
  const analysisDailyResetAt = refreshState?.analysis?.daily_reset_at;
  const lastAnalysisAt = refreshState?.analysis?.last_refresh_at;
  const pricesCooldownText = useMemo(() => formatCooldown(t, pricesNextAt), [t, pricesNextAt]);
  // Phase 8.B QA-9 — Daily limit takes precedence over the 30-minute
  // cooldown. When `refreshes_left_today === 0`, the cooldown countdown
  // is misleading because no further refresh can succeed today even
  // when the cooldown expires. Render "Available again tomorrow [at
  // HH:MM]" instead. Same precedence is honoured for the button
  // disabled state and the tooltip.
  const analysisAvailabilityText = useMemo(() => {
    if (refreshesLeft !== null && refreshesLeft <= 0) {
      if (analysisDailyResetAt) {
        try {
          const reset = new Date(analysisDailyResetAt);
          // Phase 8.B QA-9 — format the reset time using the active UI
          // locale so DE users see 24h notation (e.g. "02:00") and
          // EN users see 12h notation (e.g. "2:00 AM") naturally.
          const localeTag = (typeof navigator !== "undefined" && navigator.language)
            ? navigator.language
            : "en";
          // For DE we explicitly request 24h; for EN we let the locale decide.
          const opts = (t("portfolio.refresh.available_tomorrow") || "").startsWith("Morgen")
            ? { hour: "2-digit", minute: "2-digit", hour12: false }
            : { hour: "2-digit", minute: "2-digit" };
          const hhmm = reset.toLocaleTimeString(localeTag, opts);
          return t("portfolio.refresh.available_tomorrow_at").replace("{time}", hhmm);
        } catch {
          /* fall through */
        }
      }
      return t("portfolio.refresh.available_tomorrow");
    }
    return formatCooldown(t, analysisNextAt);
  }, [t, refreshesLeft, analysisDailyResetAt, analysisNextAt]);
  const analysisDailyLimitReached = refreshesLeft !== null && refreshesLeft <= 0;
  // Phase 8.1.c — pick the most common position currency for the
  // portfolio-level KPI cards. If positions disagree we still
  // default to the majority; users can read per-position currency
  // from the holdings table cells. Future Phase: FX conversion to
  // a chosen portfolio base currency.
  const dominantCurrency = useMemo(() => {
    if (!positions || positions.length === 0) return "USD";
    const counts = {};
    positions.forEach((p) => {
      const c = (p.currency || "USD").toUpperCase();
      counts[c] = (counts[c] || 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : "USD";
  }, [positions]);
  const noHoldings = positions.length === 0;
  const hasExistingReview = !!(review && review.review);

  const onAddClick = () => {
    setDialogMode("add");
    setDialogInitial(null);
    setDialogError(null);
    setDialogOpen(true);
  };
  const onEditHolding = (position) => {
    setDialogMode("edit");
    setDialogInitial({
      id: position.holding_id,
      ticker: position.ticker,
      shares: position.shares,
      buy_price: position.buy_price,
      buy_date: position.buy_date,
      notes: position.notes,
    });
    setDialogError(null);
    setDialogOpen(true);
  };
  const onDeleteClick = (position) => setDeleteTarget(position);

  // ---- Phase 8.C-2 callbacks ---------------------------------------
  // The HoldingsTable action menu emits one of five callbacks per row.
  // We thinly wrap the existing add/edit/delete flows and add SELL +
  // transaction-history entry points.
  const onAddPurchase = (position) => {
    setDialogMode("add");
    // Pre-fill ticker so the dialog opens scoped to this row.
    setDialogInitial({ ticker: position?.ticker });
    setDialogError(null);
    setDialogOpen(true);
  };
  const onRecordSale = (position) => {
    setSaleTarget(position);
    setSaleError(null);
    setSaleBusy(false);
    setSaleOpen(true);
  };
  const onViewTransactions = (position) => {
    setHistoryTicker(position?.ticker);
    setHistoryLifecycleId(position?.lifecycle_id || null);
    setHistoryLifecycleStatus("open");
    setHistoryCurrency(position?.currency || "USD");
    setHistoryOpen(true);
  };
  const onViewClosedHistory = (closedPosition) => {
    setHistoryTicker(closedPosition?.ticker);
    setHistoryLifecycleId(closedPosition?.lifecycle_id || null);
    setHistoryLifecycleStatus("closed");
    setHistoryCurrency(closedPosition?.currency || "USD");
    setHistoryOpen(true);
  };
  const onDeleteIncorrect = async (position) => {
    // Pre-fetch transaction count so the confirm dialog can show the
    // stronger "this position has N transactions" warning. We do NOT
    // block opening the dialog if this fetch fails — the existing
    // delete flow still functions.
    setDeleteTxnCount(0);
    try {
      const r = await api.get(`/portfolio/holdings/${encodeURIComponent(position.ticker)}/transactions`);
      setDeleteTxnCount((r?.data?.transactions || []).length);
    } catch {
      setDeleteTxnCount(0);
    }
    setDeleteTarget(position);
  };

  const fetchClosedPositions = useCallback(async () => {
    try {
      const r = await api.get("/portfolio/closed-positions");
      setClosedPositions(r?.data?.closed_positions || []);
    } catch {
      setClosedPositions([]);
    }
  }, []);

  // Refresh closed positions whenever the portfolio is re-fetched.
  useEffect(() => {
    if (user && !loading) fetchClosedPositions();
  }, [user, loading, fetchClosedPositions]);

  const submitSale = async (payload) => {
    setSaleBusy(true);
    setSaleError(null);
    try {
      const r = await api.post(
        `/portfolio/holdings/${encodeURIComponent(payload.ticker)}/sell`,
        {
          shares: payload.shares,
          price_per_share: payload.price_per_share,
          transaction_date: payload.transaction_date,
          fees: payload.fees,
          notes: payload.notes,
          idempotency_key: payload.idempotency_key,
        },
        { headers: { "X-Idempotency-Key": payload.idempotency_key } },
      );
      const wasFullClose = !!(r?.data?.transaction?.is_full_close);
      // Toasts per spec.
      toast.success(
        wasFullClose
          ? t("portfolio.toast.sale_full")
          : t("portfolio.toast.sale_partial"),
      );
      setSaleOpen(false);
      setSaleTarget(null);
      // Phase 8.B QA-11 — pin the latest review fingerprint so the
      // post-sale fetchPortfolio() is force-accepted past the
      // ordering guard (the sell event changes composition_hash so
      // the cached review's `is_stale` flag flips).
      forceAcceptNextRef.current = true;
      await Promise.all([fetchPortfolio(), fetchClosedPositions()]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = (detail && typeof detail === "object") ? detail.error_code : null;
      let msg = t("portfolio.sell.error_generic");
      if (code === "insufficient_shares") msg = t("portfolio.sell.error_insufficient");
      else if (code === "conflict") msg = t("portfolio.sell.error_conflict");
      else if (detail?.message) msg = detail.message;
      setSaleError(msg);
      // Keep the modal open so the user can correct and retry — but
      // we DO re-fetch portfolio so the available-shares header in
      // the modal reflects the latest state if a race occurred.
      if (code === "conflict" || code === "insufficient_shares") {
        try { await fetchPortfolio(); } catch { /* ignore */ }
      }
    } finally {
      setSaleBusy(false);
    }
  };

  const submitDialog = async (payload) => {
    setDialogBusy(true);
    setDialogError(null);
    try {
      if (dialogMode === "edit" && dialogInitial?.id) {
        await api.patch(`/portfolio/holdings/${dialogInitial.id}`, {
          shares: payload.shares,
          buy_price: payload.buy_price,
          buy_date: payload.buy_date,
          notes: payload.notes,
        });
        toast.success(t("portfolio.toast.holding_updated"));
      } else {
        const resp = await api.post("/portfolio/holdings", payload);
        const act = resp?.data?.action;
        const tk = resp?.data?.holding?.ticker || payload.ticker;
        if (act === "merged") {
          // Phase 8.A QA — additional purchase to existing ticker.
          toast.success(t("portfolio.toast.holding_merged", { ticker: tk }));
        } else {
          toast.success(t("portfolio.toast.holding_added"));
        }
      }
      setDialogOpen(false);
      await fetchPortfolio();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = (detail && typeof detail === "object") ? detail.error_code : null;
      // Phase 8.1.b — map known backend error codes to localized,
      // user-facing copy. Anything else falls back to a generic
      // friendly message — we NEVER show Cloudflare/provider raw text.
      const codeMap = {
        duplicate_holding: "portfolio.error.duplicate_holding",
        invalid_shares: "portfolio.dialog.err_shares",
        invalid_buy_price: "portfolio.dialog.err_buy_price",
        buy_date_required: "portfolio.dialog.err_buy_date_required",
        invalid_buy_date: "portfolio.dialog.err_buy_date",
        invalid_ticker: "portfolio.dialog.err_ticker",
        holdings_limit_reached: "portfolio.error.holdings_limit",
        premium_required: "portfolio.error.premium_required",
      };
      const friendly = code && codeMap[code]
        ? t(codeMap[code])
        : t("portfolio.error.save_failed");
      setDialogError(friendly);
    } finally {
      setDialogBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/portfolio/holdings/${deleteTarget.holding_id}`);
      toast.success(t("portfolio.toast.holding_deleted"));
      setDeleteTarget(null);
      await fetchPortfolio();
    } catch (e) {
      toast.error(t("portfolio.error.delete_failed"));
    }
  };

  const triggerRefreshPrices = async () => {
    if (refreshPricesBusy) return;
    setRefreshPricesBusy(true);
    try {
      await api.post("/portfolio/refresh-prices", { idempotency_key: newIdempotencyKey() });
      toast.success(t("portfolio.toast.prices_refreshed"));
      await fetchPortfolio();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.error_code;
      if (code === "cooldown") toast.error(t("portfolio.error.cooldown_prices"));
      else toast.error(t("portfolio.error.refresh_prices_failed"));
    } finally {
      setRefreshPricesBusy(false);
    }
  };

  const triggerRefreshAnalysis = async () => {
    if (refreshAnalysisBusy) return;
    // Phase 8.B QA-9 — daily limit takes precedence: don't fire the
    // request when the daily cap is exhausted, even if the cooldown
    // would technically allow it. The backend would reject this anyway
    // with `daily_limit_reached`; suppressing the call avoids a wasted
    // round-trip and a confusing error toast.
    if (refreshesLeft !== null && refreshesLeft <= 0) {
      toast.error(t("portfolio.error.daily_limit"));
      return;
    }
    setRefreshAnalysisBusy(true);
    try {
      const res = await api.post("/portfolio/refresh-analysis", { idempotency_key: newIdempotencyKey() });
      const status = res?.data?.status;
      const errorCode = res?.data?.error_code;
      if (status === "success") {
        toast.success(t("portfolio.toast.analysis_refreshed"));
        // Phase 8.B QA-11 — successful refresh just wrote a brand-new
        // cache row. Record its generated_at as the new lower bound
        // AND flag the next fetchPortfolio() as force-accept so the
        // ordering guard cannot block it.
        const freshGeneratedAt =
          res?.data?.stages?.generated_at
          || res?.data?.generated_at
          || (res?.data?.review || {}).generated_at
          || new Date().toISOString();
        latestReviewRef.current = {
          generatedAt: freshGeneratedAt,
          selectedId: null,
        };
        forceAcceptNextRef.current = true;
      } else if (status === "rate_limited") {
        // Backend returned a structured rate-limit signal (cooldown / daily
        // limit / hard limit / running job). Surface it as a visible toast
        // so the user understands why nothing changed visually.
        const map = {
          cooldown: "portfolio.error.cooldown_analysis",
          daily_limit_reached: "portfolio.error.daily_limit",
          hard_limit_24h: "portfolio.error.hard_limit",
          job_already_running: "portfolio.error.job_running",
        };
        toast.error(t(map[errorCode] || "portfolio.error.refresh_analysis_failed"));
      } else if (status === "failed") {
        // Phase 8.B QA-5/6 — backend executed but generation failed (LLM
        // provider error, sanitizer rejection, DB upsert error, etc.).
        // Surface the failure explicitly with the correlation_id so an
        // admin can find the matching trace in Owner Diagnostics.
        const corr = res?.data?.correlation_id;
        const stage = res?.data?.error_stage;
        toast.error(
          t("portfolio.error.refresh_analysis_failed") + (corr ? ` (id: ${corr}${stage ? `, stage: ${stage}` : ""})` : "")
        );
      } else if (status !== undefined) {
        // Any other unexpected non-success status — never fall through
        // silently.
        toast.error(t("portfolio.error.refresh_analysis_failed"));
      }
      await fetchPortfolio();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.error_code;
      const map = {
        cooldown: "portfolio.error.cooldown_analysis",
        daily_limit_reached: "portfolio.error.daily_limit",
        hard_limit_24h: "portfolio.error.hard_limit",
        job_already_running: "portfolio.error.job_running",
      };
      const key = map[code] || "portfolio.error.refresh_analysis_failed";
      toast.error(t(key));
    } finally {
      setRefreshAnalysisBusy(false);
    }
  };

  const renderHeader = (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end" data-testid="portfolio-header">
      <div className="min-w-0">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="portfolio-page-title">
          {t("portfolio.page.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl whitespace-pre-line">
          {t("portfolio.page.subtitle")}
        </p>
        {data?.admin_qa_bypass ? (
          <div className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#F0A93B] border border-[#F0A93B]/40 bg-[#F0A93B]/10 rounded-full px-2.5 py-0.5" data-testid="portfolio-admin-qa-banner">
            <AlertTriangle className="w-3 h-3" />
            Admin QA bypass (not counted as revenue)
          </div>
        ) : null}
      </div>
      {/* Phase 8.WLNAV.v1 — action row is only rendered for authenticated
          users. Logged-out visitors see the LockedPortfolioPreview below
          which owns its own signup/login CTAs; showing disabled refresh
          buttons here would just be noisy for marketing traffic. */}
      {user ? (
      <div className="flex flex-wrap items-center gap-2 min-w-0 lg:flex-nowrap lg:justify-end" data-testid="portfolio-action-row">
        <TooltipProvider delayDuration={250}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={triggerRefreshPrices}
                  disabled={refreshPricesBusy || !!pricesCooldownText || locked || noHoldings}
                  data-testid="portfolio-refresh-prices-button"
                  className="h-9 whitespace-nowrap hover:border-[#3DEBFF]/40 hover:text-[#3DEBFF]"
                >
                  {refreshPricesBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  {refreshPricesBusy ? t("portfolio.loading.prices") : t("portfolio.refresh.prices")}
                  {pricesCooldownText ? <span className="ml-1.5 text-xs text-muted-foreground">{pricesCooldownText}</span> : null}
                </Button>
              </span>
            </TooltipTrigger>
            {noHoldings ? (
              <TooltipContent data-testid="portfolio-refresh-prices-tooltip">
                {t("portfolio.refresh.tooltip_no_holdings")}
              </TooltipContent>
            ) : null}
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="sm"
                  onClick={triggerRefreshAnalysis}
                  disabled={refreshAnalysisBusy || analysisDailyLimitReached || !!analysisAvailabilityText || locked || noHoldings}
                  data-testid="portfolio-refresh-analysis-button"
                  className="h-9 whitespace-nowrap shadow-[0_0_0_1.5px_rgba(61,235,255,0.65),0_0_28px_rgba(61,235,255,0.35)] font-medium"
                >
                  {refreshAnalysisBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  {hasExistingReview
                    ? (t("portfolio.review.cta_update") || "Update Portfolio Analysis")
                    : (t("portfolio.review.cta_analyze") || "Analyze Portfolio")}
                  <span className="ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-[#0F2A30] text-[#7CECFF] border border-[#3DEBFF] font-semibold shadow-[0_0_6px_rgba(61,235,255,0.25)] whitespace-nowrap" data-testid="portfolio-cta-premium-ai-badge">
                    {t("portfolio.review.badge_premium_ai") || "Premium AI"}
                  </span>
                </Button>
              </span>
            </TooltipTrigger>
            {(noHoldings || analysisDailyLimitReached) ? (
              <TooltipContent data-testid="portfolio-refresh-analysis-tooltip">
                {noHoldings
                  ? t("portfolio.refresh.tooltip_no_holdings")
                  : t("portfolio.refresh.tooltip_daily_limit_reached")}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
        {!locked ? (
          <Button onClick={onAddClick} size="sm" data-testid="portfolio-add-holding-button" className="h-9 whitespace-nowrap">
            <Plus className="w-4 h-4 mr-1.5" />
            {t("portfolio.actions.add_holding")}
          </Button>
        ) : null}
        {/* Phase 8.PH.FINAL — Premium PDF / CSV export menu.
            Rendered only when the portfolio is unlocked (Premium user
            or admin QA bypass). Non-Premium users never see the button;
            the backend also enforces this via a 403 on the endpoint. */}
        {!locked ? (
          <PortfolioExportMenu
            visible
            disabled={noHoldings}
          />
        ) : null}
      </div>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main
        className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 w-full space-y-6 sm:space-y-8"
        data-testid="portfolio-page"
      >
        {renderHeader}

        {loadingPortfolio ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center" data-testid="portfolio-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t("portfolio.common.loading")}</span>
          </div>
        ) : fetchError ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="portfolio-load-error">
            <AlertTriangle className="w-5 h-5 inline mr-2" />
            {fetchError}
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={fetchPortfolio}>{t("portfolio.actions.retry")}</Button>
            </div>
          </div>
        ) : locked ? (
          <LockedPortfolioPreview
            onUpgrade={() => navigate("/pricing")}
            onViewPricing={() => navigate("/pricing")}
          />
        ) : !user ? (
          // Phase 8.WLNAV.v1 — logged-out preview. Do NOT redirect;
          // instead render the same blurred shell with a signup / login
          // CTA. CTAs go to /signup and /login (NOT checkout).
          <LockedPortfolioPreview
            variant="logged_out"
            onSignup={() => navigate("/signup")}
            onLogin={() => navigate("/login?next=/portfolio")}
            onViewPricing={() => navigate("/pricing")}
          />
        ) : positions.length === 0 && closedPositions.length > 0 ? (
          // Phase 8.C-3 — UX gap fix: user has sold ALL open positions but
          // still has a closed-position history. Show a "no open positions"
          // banner with the Add Holding CTA, then surface the closed
          // history (and any meaningful realized-P/L breakdown) below.
          // Crucially we do NOT swallow the closed history into the
          // generic first-time empty state.
          <div className="space-y-6 sm:space-y-8" data-testid="portfolio-no-open-positions-with-history">
            <div
              className="rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.85)] shadow-[var(--app-shadow-elev-1)] p-6 sm:p-8 text-center"
              data-testid="portfolio-no-open-positions-banner"
            >
              <div
                className="font-display text-xl sm:text-2xl tracking-tight mb-2"
                data-testid="portfolio-no-open-positions-heading"
              >
                {t("portfolio.no_open_with_history.heading")}
              </div>
              <p
                className="text-sm text-muted-foreground mb-5 max-w-xl mx-auto"
                data-testid="portfolio-no-open-positions-subtext"
              >
                {t("portfolio.no_open_with_history.subtext")}
              </p>
              <Button
                onClick={onAddClick}
                data-testid="portfolio-no-open-positions-add-holding-button"
                className="shadow-[0_0_0_1.5px_rgba(61,235,255,0.55)]"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {t("portfolio.empty.cta")}
              </Button>
            </div>
            {data?.pl_breakdown ? (
              <PLBreakdownCard
                breakdown={data.pl_breakdown}
                dominantCurrency={dominantCurrency}
              />
            ) : null}
            <ClosedPositionsTable
              closedPositions={closedPositions}
              onViewHistory={onViewClosedHistory}
            />
            {review ? (
              <PortfolioAIReview
                review={review.review}
                generatedAt={review.generated_at}
                loading={refreshAnalysisBusy}
                cached={true}
                reviewMeta={data?.review_meta || null}
              />
            ) : null}
            <div className="text-[11px] text-muted-foreground text-center pt-4">
              {t("portfolio.page.disclaimer")}
            </div>
          </div>
        ) : positions.length === 0 ? (
          <EmptyPortfolioState onAdd={onAddClick} onLearnMore={() => setScoringHelpOpen(true)} />
        ) : (
          <PortfolioDashboardView
            data={data}
            performanceRevision={performanceRevision}
            closedPositions={closedPositions}
            dominantCurrency={dominantCurrency}
            previewMode={false}
            refreshAnalysisBusy={refreshAnalysisBusy}
            onAddPurchase={onAddPurchase}
            onRecordSale={onRecordSale}
            onViewTransactions={onViewTransactions}
            onEditMetadata={onEditHolding}
            onDeleteIncorrect={onDeleteIncorrect}
            onViewClosedHistory={onViewClosedHistory}
          />
        )}
      </main>
      {positions.length > 0 && positions[0]?.ws7_metadata && (
        <div className="max-w-6xl mx-auto px-4 pb-6" data-testid="portfolio-ws7-panel">
          <TransparencyPanel metadata={positions[0].ws7_metadata} />
        </div>
      )}
      <Footer />

      <HoldingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        initial={dialogInitial}
        busy={dialogBusy}
        errorMessage={dialogError}
        onSubmit={submitDialog}
        existingPositions={positions}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteTxnCount(0); } }}>
        <AlertDialogContent data-testid="portfolio-delete-holding-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("portfolio.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTxnCount > 0
                ? t("portfolio.delete.body_with_history")
                    .replace("{ticker}", deleteTarget?.ticker || "")
                    .replace("{count}", String(deleteTxnCount))
                : t("portfolio.delete.body").replace("{ticker}", deleteTarget?.ticker || "")}
            </AlertDialogDescription>
            {deleteTxnCount > 0 ? (
              <div
                data-testid="portfolio-delete-holding-strong-warning"
                className="mt-2 rounded-md border border-[#FF3DA6]/40 bg-[#FF3DA6]/10 px-3 py-2 text-[11px] text-[#FF7AC3] flex items-start gap-2"
              >
                <span>{t("portfolio.delete_warning_use_sale")}</span>
              </div>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="portfolio-delete-holding-cancel-button">{t("portfolio.delete.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="portfolio-delete-holding-confirm-button"
              className="bg-[#FF3DA6] hover:bg-[#FF3DA6]/90 text-white"
            >
              {t("portfolio.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecordSaleModal
        open={saleOpen}
        onOpenChange={(v) => { setSaleOpen(v); if (!v) { setSaleTarget(null); setSaleError(null); } }}
        position={saleTarget}
        busy={saleBusy}
        errorMessage={saleError}
        onSubmit={submitSale}
      />

      <TransactionHistoryModal
        open={historyOpen}
        onOpenChange={(v) => { setHistoryOpen(v); if (!v) { setHistoryTicker(null); setHistoryLifecycleId(null); } }}
        ticker={historyTicker}
        lifecycleId={historyLifecycleId}
        lifecycleStatus={historyLifecycleStatus}
        currency={historyCurrency}
        onDateSaved={async () => {
          setPerformanceRevision((revision) => revision + 1);
          await fetchPortfolio();
        }}
      />

      <ScoringHelpDialog open={scoringHelpOpen} onOpenChange={setScoringHelpOpen} />
    </div>
  );
}

